/**
 * viewIr kit builders for the a2ui core-18 components that were absent from our
 * registry (AudioPlayer, CheckBox, ChoicePicker, DateTimeInput, List, Modal,
 * Slider, Tabs, TextField). Props are the basic catalog verbatim; the visuals
 * reuse the shared a2ui core controls so sessions and widgets render identically.
 *
 * viewIr component contract: `{ props (resolved), data (node.bind), emit,
 * children (rendered), renderById }`.
 */
import { useState } from 'react';
import {
  ViAudioPlayer, ViCheckBox, ViChoicePicker, ViDateTimeInput, ViSlider, ViTextField,
} from '../../../../a2ui/coreControls';

export function List({ props = {}, children }) {
  const row = props.direction === 'horizontal';
  return (
    <div data-a2ui-list style={{
      display: 'flex', flexDirection: row ? 'row' : 'column', gap: 8,
      alignItems: props.align === 'center' ? 'center' : props.align === 'end' ? 'flex-end' : 'stretch',
      flexWrap: row ? 'wrap' : 'nowrap',
    }}>
      {children}
    </div>
  );
}

export function Modal({ props = {}, renderById }) {
  const one = (id) => (typeof id === 'string' && renderById ? renderById(id) : id);
  return (
    <div data-a2ui-modal style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {one(props.trigger)}
      {one(props.content)}
    </div>
  );
}

export function Tabs({ props = {}, renderById }) {
  const tabs = Array.isArray(props.tabs) ? props.tabs : [];
  return (
    <div data-a2ui-tabs style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {tabs.map((t, i) => (
        <div key={i}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{t && typeof t === 'object' ? t.title : String(t)}</div>
          {t && typeof t === 'object' && typeof t.child === 'string' && renderById ? renderById(t.child) : null}
        </div>
      ))}
    </div>
  );
}

export function AudioPlayer({ props = {}, data }) {
  return <ViAudioPlayer url={props.url ?? data} description={props.description} />;
}

/**
 * The value a control shows: the user's last edit until the bound value itself
 * changes (a re-read that disagrees wins), so a tap shows in the same frame.
 */
function useEcho(bound) {
  const [echo, setEcho] = useState({ bound, value: bound });
  if (echo.bound !== bound) {
    setEcho({ bound, value: bound });
    return [bound, setEcho];
  }
  return [echo.value, setEcho];
}

export function CheckBox({ variant, props = {}, data, emit }) {
  const bound = Boolean(data != null ? data : props.value);
  const [checked, setEcho] = useEcho(bound);
  const onChange = (v) => { setEcho({ bound, value: v }); emit?.('changed', { value: v }); };
  if (variant === 'switch') return <ViSwitch label={props.label} checked={checked} onChange={onChange} />;
  return <ViCheckBox label={props.label} checked={checked} onChange={onChange} />;
}

function ViSwitch({ label, checked, onChange }) {
  return (
    <button type="button" role="switch" aria-checked={checked ? 'true' : 'false'} aria-label={label || 'Toggle'}
      onClick={() => onChange(!checked)}
      style={{
        appearance: 'none', border: 'none', background: 'none', padding: 0, margin: 0, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-2)', color: 'var(--text)', font: 'inherit',
      }}>
      {label ? <span style={{ fontSize: 'var(--t-md)' }}>{label}</span> : null}
      <span data-switch-track style={{
        position: 'relative', width: 51, height: 31, borderRadius: 999, flex: '0 0 51px',
        background: checked ? 'var(--good)' : 'var(--surface-3)', transition: 'background 160ms var(--ease)',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: checked ? 22 : 2, width: 27, height: 27, borderRadius: '50%',
          background: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', transition: 'left 160ms var(--ease)',
        }} />
      </span>
    </button>
  );
}

export function Slider({ props = {}, data, emit }) {
  return <ViSlider label={props.label} value={data != null ? data : props.value} min={props.min} max={props.max} step={props.steps}
    onChange={(v) => emit?.('changed', { value: v })} />;
}

export function TextField({ props = {}, data, emit }) {
  const bound = data != null ? data : (props.value ?? '');
  const [text, setEcho] = useEcho(bound);
  return <ViTextField label={props.label} value={text} placeholder={props.placeholder} variant={props.variant}
    onChange={(v) => {
      setEcho({ bound, value: v });
      emit?.('changed', { value: v });
      emit?.('textChanged', { text: v });
    }}
    onCommit={(v) => emit?.('committed', { value: v })} />;
}

export function ChoicePicker({ props = {}, data, emit }) {
  return <ViChoicePicker label={props.label} options={props.options} value={data != null ? data : props.value}
    multiple={props.variant === 'multipleSelection'} displayStyle={props.displayStyle}
    onChange={(v) => emit?.('changed', { value: v })} />;
}

export function DateTimeInput({ props = {}, data, emit }) {
  const bound = data != null ? data : (props.value ?? null);
  const [value, setEcho] = useEcho(bound);
  const enableTime = props.enableTime === true;
  return <ViDateTimeInput label={props.label} value={value}
    enableDate={enableTime ? props.enableDate === true : props.enableDate !== false} enableTime={enableTime}
    onChange={(v) => { setEcho({ bound, value: v }); emit?.('changed', { value: v }); }} />;
}
